import React, { useEffect } from 'react';
import { FaUser, FaInfoCircle, FaCalendarAlt, FaBuilding, FaUserTie, FaPhone, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaPaperPlane, FaIdBadge } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequestUserAccount } from "redux/request/userAccountSlice";
import RequestUserAccountDetailHeader from '../../../components/app/account-management/request-user-account/RequestUserAccountDetailHeader';
import { formatDate } from "helpers/utils";
import { MdHistoryToggleOff, MdComment, MdClose } from "react-icons/md";
import { BsCheckCircleFill } from "react-icons/bs";
import { Badge, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  Completed:   { icon: FaCheckCircle,    bg: 'tw-bg-green-50',  border: 'tw-border-green-200',  iconBg: 'tw-bg-green-500',  textColor: 'tw-text-green-800',  badgeVariant: 'success', msg: 'Your request has been completed successfully.' },
  Rejected:    { icon: FaTimesCircle,    bg: 'tw-bg-red-50',    border: 'tw-border-red-200',    iconBg: 'tw-bg-red-500',    textColor: 'tw-text-red-800',    badgeVariant: 'danger',  msg: 'Your request has been closed and rejected.' },
  'In progress':{ icon: FaHourglassHalf, bg: 'tw-bg-amber-50',  border: 'tw-border-amber-200',  iconBg: 'tw-bg-amber-500',  textColor: 'tw-text-amber-800',  badgeVariant: 'warning', msg: 'Your request is currently being processed by the IT department.' },
  Submitted:   { icon: FaPaperPlane,     bg: 'tw-bg-blue-50',   border: 'tw-border-blue-200',   iconBg: 'tw-bg-blue-500',   textColor: 'tw-text-blue-800',   badgeVariant: 'info',    msg: 'Your request has been submitted to the IT department and is awaiting processing.' },
};
const DEFAULT_STATUS = STATUS_CONFIG.Submitted;

// ── Primitives ────────────────────────────────────────────────────────────────
const SectionCard = ({ title, accentColor = 'tw-bg-blue-500', icon: Icon, children }) => (
  <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden tw-mb-4">
    <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-py-3 tw-bg-slate-50 tw-border-b tw-border-slate-100">
      <div className={`tw-w-1 tw-h-4 tw-rounded-xl ${accentColor}`} />
      {Icon && <Icon size={13} className="tw-text-slate-500" />}
      <h5 className="tw-text-sm tw-font-bold tw-text-slate-700 tw-m-0">{title}</h5>
    </div>
    <div className="tw-divide-y tw-divide-slate-50">{children}</div>
  </div>
);
SectionCard.propTypes = { title: PropTypes.string.isRequired, accentColor: PropTypes.string, icon: PropTypes.elementType, children: PropTypes.node.isRequired };

const InfoRow = ({ label, value, icon: Icon, isLink = false, children }) => (
  <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 hover:tw-bg-slate-50/60 tw-transition-colors">
    {Icon && (
      <div className="tw-w-7 tw-h-7 tw-rounded tw-bg-slate-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5">
        <Icon size={12} className="tw-text-slate-500" />
      </div>
    )}
    <div className="tw-flex-1 tw-min-w-0">
      <p className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-mb-0.5 tw-m-0">{label}</p>
      {children ? (
        <div className="tw-mt-1">{children}</div>
      ) : (
        <div className="tw-text-sm tw-font-semibold tw-text-slate-800">
          {isLink && value
            ? <a href={`tel:${value}`} className="tw-text-blue-600 tw-no-underline hover:tw-underline">{value}</a>
            : <span>{value || '—'}</span>
          }
        </div>
      )}
    </div>
  </div>
);
InfoRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string, icon: PropTypes.elementType, isLink: PropTypes.bool, children: PropTypes.node };

// ── Page ──────────────────────────────────────────────────────────────────────
const RequestUserAccountDetailPage = () => {
  const { recordData: record, loading } = useSelector((state) => state.request_user_account);
  const params = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        await dispatch(getRequestUserAccount(params.id)).unwrap();
      } catch (error) {
        toast.error(`Error fetching user account details: ${error.message}`);
      }
    };
    fetchRecord();
  }, [dispatch, params]);

  if (loading) return <LoadingSpinner size="md" message="Loading request details..." />;
  if (!record) return null;

  const statusCfg = STATUS_CONFIG[record.status] || DEFAULT_STATUS;
  const StatusIcon = statusCfg.icon;

  return (
    <>
      <RequestUserAccountDetailHeader record={record} />

      {/* ── Status Banner ── */}
      <div className={`tw-rounded-2xl tw-border tw-p-4 tw-mb-4 ${statusCfg.bg} ${statusCfg.border}`}>
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className={`tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${statusCfg.iconBg}`}>
            <StatusIcon size={18} className="tw-text-white" />
          </div>
          <div className="tw-flex-1">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-0.5">
              <span className={`tw-text-sm tw-font-bold ${statusCfg.textColor}`}>{record.status}</span>
              <Badge variant={statusCfg.badgeVariant} size="sm">{record.status}</Badge>
            </div>
            <p className={`tw-text-xs tw-m-0 ${statusCfg.textColor}`}>{statusCfg.msg}</p>
          </div>
        </div>
      </div>

      {/* ── Meta strip ── */}
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-px-5 tw-py-3.5 tw-mb-4">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
              <FaInfoCircle size={14} className="tw-text-blue-600" />
            </div>
            <div>
              <p className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-m-0">Request</p>
              <p className="tw-text-sm tw-font-bold tw-text-slate-800 tw-m-0">User Account Information</p>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-slate-500">
            <span className="tw-flex tw-items-center tw-gap-1">
              <FaUser size={11} />
              <span>Submitted by <strong className="tw-text-slate-700">{record.created_by || '—'}</strong></span>
            </span>
            <span className="tw-flex tw-items-center tw-gap-1">
              <MdHistoryToggleOff size={13} />
              <span>{formatDate(record.created_date) || '—'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">

        {/* Left: Personal */}
        <SectionCard title="Personal Information" accentColor="tw-bg-blue-500" icon={FaUser}>
          <InfoRow label="First Name"     value={record.first_name}           icon={FaUser} />
          <InfoRow label="Last Name"      value={record.last_name}            icon={FaUser} />
          <InfoRow label="Employee Code"  value={record.name}                 icon={FaIdBadge} />
          <InfoRow label="Gender" icon={FaUser}>
            <span className={`tw-inline-block tw-px-2.5 tw-py-0.5 tw-rounded tw-text-xs tw-font-bold tw-text-white ${
              record.gender === 'Male' ? 'tw-bg-blue-500' : 'tw-bg-pink-400'
            }`}>
              {record.gender || '—'}
            </span>
          </InfoRow>
          <InfoRow label="Job Title"      value={record.title}                icon={FaUserTie} />
          <InfoRow label="Phone Number"   value={record.phone_number}         icon={FaPhone} isLink />
          <InfoRow label="Onboard Date"   value={formatDate(record.onboard_date)} icon={FaCalendarAlt} />
        </SectionCard>

        {/* Right: Org + Purpose + Remark */}
        <div>
          <SectionCard title="Organization Details" accentColor="tw-bg-green-500" icon={FaBuilding}>
            <InfoRow label="Manager"    value={record.manager?.name}    icon={FaUserTie} />
            <InfoRow label="Department" value={record.department?.name} icon={FaBuilding} />
            <InfoRow label="Site"       value={record.site?.name}       icon={FaMapMarkerAlt} />
          </SectionCard>

          <SectionCard title="Purpose" accentColor="tw-bg-cyan-500">
            <div className="tw-px-4 tw-py-3">
              {record?.purpose && record.purpose.length > 0 ? (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {record.purpose.map((item, i) => (
                    <span key={i} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-blue-50 tw-text-blue-700 tw-text-xs tw-font-semibold tw-border tw-border-blue-100">
                      <BsCheckCircleFill size={11} className="tw-text-green-500" />
                      {item}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="tw-text-sm tw-text-slate-400 tw-m-0">No purpose specified</p>
              )}
            </div>
          </SectionCard>

          {record.remark && (
            <SectionCard title="Remark" accentColor="tw-bg-slate-400" icon={MdComment}>
              <div className="tw-px-4 tw-py-3">
                <p className="tw-text-sm tw-text-slate-700 tw-m-0 tw-leading-relaxed">{record.remark}</p>
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {/* ── Closure section ── */}
      {(record.comment_by_it || record.closed_by || record.closed_date) && (
        <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden tw-mt-4">
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-py-3 tw-bg-red-50 tw-border-b tw-border-red-100">
            <div className="tw-w-1 tw-h-4 tw-rounded-xl tw-bg-red-500" />
            <MdClose size={14} className="tw-text-red-500" />
            <h5 className="tw-text-sm tw-font-bold tw-text-red-700 tw-m-0">Closure Information</h5>
          </div>

          {record.comment_by_it && (
            <div className="tw-px-4 tw-pt-4">
              <p className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-widest tw-mb-1.5">
                <MdComment className="tw-inline tw-mr-1" />Comment by IT
              </p>
              <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-px-4 tw-py-3 tw-text-sm tw-text-red-700">
                {record.comment_by_it}
              </div>
            </div>
          )}

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-divide-y md:tw-divide-y-0 md:tw-divide-x tw-divide-slate-100 tw-mt-2">
            <InfoRow label="Closed By"   value={record.closed_by}              icon={FaUser} />
            <InfoRow label="Closed Date" value={formatDate(record.closed_date)} icon={FaCalendarAlt} />
          </div>
        </div>
      )}
    </>
  );
};

export default RequestUserAccountDetailPage;
